import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileDown, Plus, ImagePlus, Loader2, Save, Ruler, UserCog, Coins } from 'lucide-react';
import api, { API } from '@/lib/api';
import { formatRp, formatDate, todayISO, STAGES, METODE_OPTIONS, TIPE_OPTIONS } from '@/lib/format';
import { StageBadge, PaymentBadge } from '@/components/StatusBadge';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

export default function ProyekDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isField = user?.role === 'field';
  const isOwner = user?.role === 'owner';

  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [measurements, setMeasurements] = useState('');
  const [biaya, setBiaya] = useState('');
  const [users, setUsers] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ jumlah: '', metode: 'transfer', tipe: 'dp', tanggal: todayISO(), catatan: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${id}`);
      setP(r.data); setNotes(r.data.notes || ''); setMeasurements(r.data.measurements || ''); setBiaya(r.data.biaya_aktual || '');
      if (!isField) {
        try { const u = await api.get('/users'); setUsers(u.data); } catch { /* daftar pengguna opsional, abaikan kegagalan */ }
      }
    } finally { setLoading(false); }
  }, [id, isField]);
  useEffect(() => { load(); }, [load]);

  const changeStage = async (stage) => {
    try { await api.patch(`/projects/${id}/stage`, { stage }); toast.success(`Tahap: ${stage}`); load(); } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal'); }
  };
  const saveField = async (payload, msg) => {
    setSaving(true);
    try { await api.put(`/projects/${id}`, payload); toast.success(msg); load(); } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal'); } finally { setSaving(false); }
  };
  const assignTukang = async (uid) => {
    try { await api.put(`/projects/${id}`, { assigned_user_id: uid }); toast.success('Tukang ditugaskan'); load(); } catch { toast.error('Gagal'); }
  };
  const addPayment = async () => {
    if (!Number(payForm.jumlah)) { toast.error('Isi jumlah'); return; }
    setSaving(true);
    try {
      await api.post('/payments', { project_id: id, ...payForm, jumlah: Number(payForm.jumlah) });
      toast.success('Pembayaran dicatat'); setPayOpen(false); setPayForm({ jumlah: '', metode: 'transfer', tipe: 'dp', tanggal: todayISO(), catatan: '' }); load();
    } catch { toast.error('Gagal'); } finally { setSaving(false); }
  };
  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2.5 * 1024 * 1024) { toast.error('Maks 2.5MB'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try { await api.post(`/projects/${id}/photos`, { photo: reader.result }); toast.success('Foto ditambahkan'); load(); } catch { toast.error('Gagal unggah'); }
    };
    reader.readAsDataURL(file);
  };

  if (loading || !p) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const pct = (!isField && p.nilai > 0) ? Math.min(100, Math.round((p.sudah_dibayar / p.nilai) * 100)) : 0;
  const assignableUsers = users.filter((u) => u.role === 'field' || u.role === 'staff');

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(isField ? '/tugas' : '/proyek')} className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-muted"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="truncate font-display text-xl font-bold">{p.nama_proyek}</h1>
      </div>

      <div className="rounded-xl border bg-card p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-semibold">{p.customer_name}</div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <StageBadge stage={p.stage} />
              {!isField && <PaymentBadge status={p.status_pembayaran} />}
            </div>
            {p.assigned_user_name && <div className="mt-1 text-xs text-muted-foreground">Tukang: {p.assigned_user_name}</div>}
          </div>
          {!isField && (
            <div className="text-right"><div className="font-display text-lg font-bold text-primary tabular-nums">{formatRp(p.nilai)}</div><div className="text-xs text-muted-foreground">Target {formatDate(p.target_selesai)}</div></div>
          )}
        </div>
        <div className="mt-3">
          <Label className="text-xs text-muted-foreground">Ubah Tahap</Label>
          <Select value={p.stage} onValueChange={changeStage}>
            <SelectTrigger className="mt-1 h-10" data-testid="proyek-detail-stage-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Owner/Staff management card */}
      {!isField && (
        <div className="rounded-xl border bg-card p-4 space-y-3">
          <h2 className="flex items-center gap-2 font-display text-sm font-semibold"><UserCog className="h-4 w-4 text-primary" /> Kelola Proyek</h2>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Tugaskan ke Tukang/Staff</Label>
            <Select value={p.assigned_user_id || ''} onValueChange={assignTukang}>
              <SelectTrigger className="h-10" data-testid="proyek-assign-tukang"><SelectValue placeholder="Pilih tukang" /></SelectTrigger>
              <SelectContent>{assignableUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.nama} ({u.role})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {isOwner && (
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1 text-xs text-muted-foreground"><Coins className="h-3 w-3" /> Biaya Aktual / HPP (untuk profit aktual)</Label>
              <div className="flex gap-2">
                <Input type="number" value={biaya} onChange={(e) => setBiaya(e.target.value)} placeholder="0" className="h-10" data-testid="proyek-biaya-aktual" />
                <Button onClick={() => saveField({ biaya_aktual: Number(biaya) || 0 }, 'Biaya aktual disimpan')} disabled={saving} className="h-10 rounded-xl">Simpan</Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Tabs defaultValue={isField ? 'ukuran' : 'ringkasan'}>
        <TabsList className={`grid w-full ${isField ? 'grid-cols-3' : 'grid-cols-4'}`}>
          {!isField && <TabsTrigger value="ringkasan">Ringkasan</TabsTrigger>}
          {!isField && <TabsTrigger value="pembayaran">Bayar</TabsTrigger>}
          <TabsTrigger value="ukuran">Ukuran</TabsTrigger>
          <TabsTrigger value="foto">Foto</TabsTrigger>
          <TabsTrigger value="catatan">Catatan</TabsTrigger>
        </TabsList>

        {!isField && (
          <TabsContent value="ringkasan" className="mt-3 space-y-3">
            <div className="rounded-xl border bg-card p-4">
              <Progress value={pct} className="h-2.5" />
              <div className="mt-2 flex justify-between text-sm"><span className="text-muted-foreground">Dibayar {formatRp(p.sudah_dibayar)}</span><span className={p.sisa > 0 ? 'font-semibold text-orange-600' : 'font-semibold text-emerald-600'}>{p.sisa > 0 ? `Sisa ${formatRp(p.sisa)}` : 'Lunas'}</span></div>
            </div>
            {p.quote && (
              <div className="flex items-center justify-between rounded-xl border bg-card p-4">
                <div><div className="text-sm font-medium">Penawaran {p.quote.nomor}</div><div className="text-xs text-muted-foreground">{p.quote.line_items?.length || 0} item</div></div>
                <Button variant="outline" size="sm" onClick={() => window.open(`${API}/quotes/${p.quote.id}/pdf`, '_blank')} className="rounded-lg"><FileDown className="mr-1.5 h-4 w-4" /> PDF</Button>
              </div>
            )}
          </TabsContent>
        )}

        {!isField && (
          <TabsContent value="pembayaran" className="mt-3 space-y-3">
            <Button data-testid="proyek-detail-catat-bayar" onClick={() => setPayOpen(true)} className="rounded-xl"><Plus className="mr-1.5 h-4 w-4" /> Catat Pembayaran</Button>
            {(p.payments || []).length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground">Belum ada pembayaran.</p> : (
              <div className="space-y-2">
                {p.payments.map((pay) => (
                  <div key={pay.id} className="flex items-center justify-between rounded-xl border bg-card p-3">
                    <div><div className="text-sm font-medium capitalize">{pay.tipe} · {pay.metode}</div><div className="text-xs text-muted-foreground">{formatDate(pay.tanggal)} {pay.catatan ? `· ${pay.catatan}` : ''}</div></div>
                    <div className="font-display font-semibold text-emerald-600 tabular-nums">{formatRp(pay.jumlah)}</div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        <TabsContent value="ukuran" className="mt-3 space-y-3">
          <div className="rounded-xl border bg-card p-4">
            <Label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium"><Ruler className="h-4 w-4 text-primary" /> Hasil Ukur di Lokasi</Label>
            <Textarea value={measurements} onChange={(e) => setMeasurements(e.target.value)} rows={6} data-testid="proyek-ukuran-text"
              placeholder="cth: Jendela 1: 150cm x 120cm. Pintu: 90cm x 210cm. Catatan: dinding tidak rata 2cm di sisi kiri." />
            <Button onClick={() => saveField({ measurements }, 'Ukuran disimpan')} disabled={saving} className="mt-2 rounded-xl" data-testid="proyek-ukuran-simpan">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Simpan Ukuran
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="foto" className="mt-3 space-y-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-muted">
            <ImagePlus className="h-4 w-4" /> Unggah Foto
            <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="proyek-foto-input" />
          </label>
          {(p.photos || []).length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground">Belum ada foto (before/after).</p> : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {p.photos.map((ph, i) => <img key={`${i}-${(ph || '').slice(-24)}`} src={ph} alt={`Foto ${i + 1}`} className="aspect-square w-full rounded-xl border object-cover" />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="catatan" className="mt-3 space-y-3">
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} placeholder="Catatan proyek..." />
          <Button onClick={() => saveField({ notes }, 'Catatan disimpan')} disabled={saving} className="rounded-xl">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Simpan Catatan</Button>
        </TabsContent>
      </Tabs>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Catat Pembayaran</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Jumlah (Rp)</Label><Input type="number" value={payForm.jumlah} onChange={(e) => setPayForm({ ...payForm, jumlah: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Tanggal</Label><Input type="date" value={payForm.tanggal} onChange={(e) => setPayForm({ ...payForm, tanggal: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Jenis</Label><Select value={payForm.tipe} onValueChange={(v) => setPayForm({ ...payForm, tipe: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TIPE_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Metode</Label><Select value={payForm.metode} onValueChange={(v) => setPayForm({ ...payForm, metode: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{METODE_OPTIONS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter><Button onClick={addPayment} disabled={saving} className="rounded-xl">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
